// 092 -- Admin Shop Providers list page. Super-admin only.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { ProviderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Breadcrumbs } from "@/components/breadcrumbs";

export default function AdminShopProvidersPage() {
  const location = useLocation();
  const [providers, setProviders] = useState<ProviderPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  useEffect(() => {
    api.getAdminProviders()
      .then((r) => setProviders(r.providers))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"));
  }, [location.key]);

  const filtered = useMemo(() => {
    if (!providers) return [];
    const q = search.trim().toLowerCase();
    return providers.filter((p) => {
      if (statusFilter === "active" && !p.active) return false;
      if (statusFilter === "inactive" && p.active) return false;
      if (q && !p.name.toLowerCase().includes(q) && !p.slug.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [providers, search, statusFilter]);

  return (
    <div
      data-page="admin-shop-providers"
      data-state={providers === null ? "loading" : error ? "error" : "ready"}
      className="flex flex-col gap-6"
    >
      <Breadcrumbs items={[{ label: "Admin" }, { label: "Shop providers" }]} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Shop providers</h1>
          <p className="text-sm text-muted-foreground">
            Onboard new providers. Product catalogs are per-provider and only apply to order-email fulfilment.
          </p>
        </div>
        <Link to="/admin/shop/providers/new" className={buttonVariants({ size: "sm" })} data-action="new-provider">
          + New provider
        </Link>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <div className="flex flex-wrap gap-3">
        <Input
          data-field="search"
          placeholder="Search by name or slug"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex gap-1">
          {(["all", "active", "inactive"] as const).map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(s)}
              data-action={`filter-${s}`}
            >
              {s === "all" ? "All" : s === "active" ? "Active" : "Inactive"}
            </Button>
          ))}
        </div>
      </div>

      {providers === null ? (
        <p className="text-muted-foreground" data-state="loading">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No providers match the current filters.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-region="providers">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">Type</th>
                  <th className="py-2 px-4 text-left">Slug</th>
                  <th className="py-2 px-4 text-center">Active</th>
                  <th className="py-2 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-border/60" data-item="provider" data-item-id={p.id}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {p.logo_url && <img src={p.logo_url} alt="" className="h-6 w-6 object-contain" />}
                        <Link to={`/admin/shop/providers/${p.id}/edit`} className="font-medium text-primary hover:underline">
                          {p.name}
                        </Link>
                      </div>
                      {p.discount_blurb && <p className="text-xs text-muted-foreground">{p.discount_blurb}</p>}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs">
                        {p.fulfillment_type.replace("_", " ")}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{p.slug}</td>
                    <td className="py-3 px-4 text-center">
                      {p.active ? (
                        <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">Active</span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">Inactive</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Link
                        to={`/admin/shop/providers/${p.id}/edit`}
                        className={buttonVariants({ variant: "outline", size: "sm" })}
                        data-action="edit"
                      >
                        Edit
                      </Link>
                      {p.fulfillment_type === "order_email" && (
                        <Link
                          to={`/admin/shop/providers/${p.id}/products`}
                          className={buttonVariants({ variant: "outline", size: "sm" })}
                          data-action="products"
                        >
                          Products
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
